import { useQuery, useQueryClient } from '@tanstack/react-query';
import { logEvent } from '@util/analytics';
import { analyticsPromise, AUTH, realtimeDatabase, perf } from '@util/firebase';
import {
  addToInteractions,
  createUserDocument,
  getUserById,
  getValidUserDocument,
  updateUserPlatforms,
} from '@util/firestore/users';
import { checkInAppBrowser } from '@util/hooks/checkInAppBrowser';
import { useOnlineStatus } from '@util/hooks/useOnlineStatus';
import useRealTimeUser from '@util/hooks/useRealtimeUser';
import { getHostUrl, isMobile } from '@util/index';
import { logError } from '@util/logError';
import { setUserId } from 'firebase/analytics';
import { FirebaseError } from 'firebase/app';
import {
  linkWithCredential as _linkWithCredential,
  sendEmailVerification as _sendEmailVerification,
  sendPasswordResetEmail as _sendPasswordResetEmail,
  ActionCodeSettings,
  applyActionCode,
  AuthErrorCodes,
  browserLocalPersistence,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  FacebookAuthProvider,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  linkWithPopup,
  OAuthProvider,
  onIdTokenChanged,
  PhoneAuthProvider,
  reauthenticateWithCredential,
  RecaptchaVerifier,
  setPersistence,
  signInAnonymously,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateEmail,
  updatePassword,
  User,
  UserCredential,
} from 'firebase/auth';
import {
  get,
  onDisconnect,
  onValue,
  ref,
  serverTimestamp,
  update,
} from 'firebase/database';
import { default as Cookie, default as Cookies } from 'js-cookie';
import { PerformanceTrace, trace } from 'firebase/performance';
import { UserAttribution, UserDocument } from 'models/user';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

// import algoliaInsights from 'search-insights';

interface AuthProviderProps {
  children: ReactNode;
}

export interface Credentials {
  email: string;
  password: string;
}

export type FirebaseAuthFn<T extends Credentials | AuthProvider> = (
  args: T
) => Promise<FirebaseError | UserCredential>;

export const EMAIL_ERRORS = [
  AuthErrorCodes.USER_DELETED,
  AuthErrorCodes.EMAIL_EXISTS,
  AuthErrorCodes.INVALID_EMAIL,
  AuthErrorCodes.UNVERIFIED_EMAIL,
  AuthErrorCodes.INVALID_RECIPIENT_EMAIL,
];

export const PASSWORD_ERRORS = [
  AuthErrorCodes.WEAK_PASSWORD,
  AuthErrorCodes.INVALID_PASSWORD,
];

type AuthProvider = 'google' | 'facebook' | 'apple';

interface AuthContext {
  user: User | null | undefined;
  userDoc?: UserDocument | null;
  isLoadingUserDoc: boolean;
  isLoggingIn: boolean;
  logout: () => Promise<void>;
  loginEmailPassword: FirebaseAuthFn<Credentials>;
  loginSocial: FirebaseAuthFn<AuthProvider>;
  loginSocialRedirect: (args: AuthProvider) => Promise<void | FirebaseError>;
  createAccountEmailPassword: FirebaseAuthFn<Credentials>;
  signInWithToken: FirebaseAuthFn<Credentials>;
  sendEmailVerification: (user?: User | null) => Promise<void>;
  linkWithCredential: (email: string) => Promise<void>;
  linkWithCredentialSocial: (
    providerName: AuthProvider
  ) => Promise<UserCredential | { user: User }>;
  sendPasswordResetEmail: (email: string) => void;
  resetPassword: (oobCode: string, password: string) => Promise<void>;
  updateUserPassword: (oldPassword: string, password: string) => Promise<void>;
  isAccountEmailPassword: () => boolean;
  verifyEmail: (actionCode: string) => Promise<void>;
  updateUserEmail: (email: string) => void;
  refreshUserDoc: (uid?: string) => Promise<void>;
  sendPhoneVerification: (phoneNumber: string) => Promise<string>;
  signInWithPhone: (id: string, code: string) => Promise<UserCredential>;
  linkPhone: (id: string, code: string) => Promise<UserCredential>;
  showWelcomeBackModal: boolean;
  setShowWelcomeBackModal: (show: boolean) => void;
  observeProduct: (productId: string) => void;
  signInAnon: () => Promise<void>;
  attribution: UserAttribution | null;
  getUserPhoneNumber: () => Promise<string | null>;
  startPerfTrace: (name: string) => {
    stop: (attributes?: Record<string, string | number>) => number;
  };
}
const AuthContext = createContext({} as AuthContext);

export const initAuthProvider = (p: AuthProvider) => {
  if (p === 'apple') return new OAuthProvider('apple.com');
  if (p === 'facebook') return new FacebookAuthProvider();
  return new GoogleAuthProvider();
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null | undefined>();
  const [showWelcomeBackModal, setShowWelcomeBackModal] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const { setOffline } = useOnlineStatus();
  const queryClient = useQueryClient();

  // Performance tracing helper for auth/login flows
  const startPerfTrace = (name: string) => {
    let t: PerformanceTrace | null = null;
    const startedAt = Date.now();
    if (perf) {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('startPerfTrace', name);
        }
        t = trace(perf, name);
        t.start();
      } catch {}
    }
    return {
      stop: (attributes?: Record<string, string | number>) => {
        const durationMs = Date.now() - startedAt;
        if (t) {
          if (attributes) {
            Object.entries(attributes).forEach(([key, value]) => {
              try {
                t!.putAttribute(key, String(value));
              } catch {}
            });
          }
          try {
            if (process.env.NODE_ENV === 'development') {
              console.log('stopPerfTrace', name);
            }
            t.stop();
          } catch {}
        }
        if (durationMs > 10000) {
          logError(
            new Error('Slow auth trace'),
            undefined,
            undefined,
            `${name} took ${durationMs}ms`
          );
        }
        return durationMs;
      },
    };
  };

  const [attribution, setAttribution] = useState<UserAttribution | null>(null);
  // attribution tracker
  useEffect(() => {
    if (attribution) return;
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    const medium = params.get('utm_medium');
    const campaign = params.get('utm_campaign');
    const term = params.get('utm_term');
    const content = params.get('utm_content');
    const id = params.get('utm_id');
    const fbclid = params.get('fbclid');
    if (source) {
      setAttribution({
        source,
        medium,
        campaign,
        term,
        content,
        id,
        fbclid,
      });
    }
  }, [attribution]);
  useEffect(() => {
    let recaptchaVerifier: RecaptchaVerifier | null = null;
    if (typeof window !== 'undefined' && !window.recaptchaVerifier) {
      try {
        let recaptchaContainer = document.getElementById('recaptcha-container');

        if (!recaptchaContainer) {
          recaptchaContainer = document.createElement('div');
          recaptchaContainer.id = 'recaptcha-container';
          recaptchaContainer.className = 'sr-only';
          document.body.prepend(recaptchaContainer);
        }

        recaptchaVerifier = new RecaptchaVerifier(AUTH, recaptchaContainer, {
          size: 'invisible',
        });

        recaptchaVerifier.render().then(() => {
          if (recaptchaVerifier) {
            window.recaptchaVerifier = recaptchaVerifier;
          }
        });
      } catch (e) {
        logError(e, 'recaptchaVerifier');
      }
    }
    return () => {
      if (typeof window !== 'undefined' && window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
          delete window.recaptchaVerifier;
        } catch (e) {
          logError(e, 'recaptchaVerifier cleanup');
        }
      }
    };
  }, []);
  // get user document
  const { data: userDoc, isLoading: isLoadingUserDoc } = useQuery({
    queryKey: ['authUser', user?.uid],
    queryFn: () => {
      if (!user?.uid) {
        return null;
      }
      return getUserById(user.uid);
    },
  });
  const realTimeUser = useRealTimeUser(userDoc?.uid);
  const logout = useCallback(async () => {
    const impersonated = Cookies.get('impersonated');
    if (!impersonated) {
      await setOffline(user?.uid);
    }
    await signOut(AUTH);
    setUser(null);
    queryClient.setQueryData(['authUser'], null);
    Cookie.remove('auth');
    Cookie.remove('algoliaUserToken');
    Cookie.remove('impersonated');
  }, [setOffline, user?.uid, queryClient]);
  useEffect(() => {
    if (realTimeUser?.banned) {
      logout();
    }
  }, [logout, realTimeUser]);

  useQuery({
    queryKey: ['ip', user?.uid],
    queryFn: () => {
      fetch('/api/ip')
        .then((res) => {
          const ip =
            res.headers.get('x-client-ip')?.replaceAll('.', '_') || 'idk';
          function setOnlineStatus(uid: string) {
            const statusRef = ref(realtimeDatabase, `/status/${uid}`);
            const last_changed = serverTimestamp();
            update(statusRef, {
              state: 'online',
              web: last_changed,
              web_info: { ua: navigator.userAgent },
              last_changed,
              ['access_logs/' + ip + '/connected']: last_changed,
            }).catch((e) => {
              logError(
                e,
                undefined,
                undefined,
                JSON.stringify({ uid, ip, msg: 'set online status' })
              );
            });
            const infoConnectedRef = ref(realtimeDatabase, '.info/connected');
            onValue(infoConnectedRef, (snapshot) => {
              const isOnline = snapshot.val();
              if (isOnline) {
                const last_changed = serverTimestamp();
                onDisconnect(statusRef)
                  .update({
                    state: 'offline',
                    web: last_changed,
                    last_changed,
                    ['access_logs/' + ip + '/disconnected']: last_changed,
                  })
                  .catch((e) => {
                    logError(
                      e,
                      undefined,
                      undefined,
                      JSON.stringify({ uid, ip, msg: 'set offline status' })
                    );
                  });
              }
            });
          }
          const onSuccess = async (uid?: string) => {
            if (user?.uid) {
              if (uid) {
                const statusRef = ref(realtimeDatabase, `/status/${user?.uid}`);
                // get data from ref
                const status = await get(statusRef);
                const last_changed = status.val()?.last_changed;
                const shouldShowWelcomeBackModal =
                  last_changed < Date.now() - 1000 * 60 * 60 * 24 * 14;
                const isImpersonated = Cookies.get('impersonated');
                if (shouldShowWelcomeBackModal && !isImpersonated) {
                  logEvent('welcome_back_modal_shown', {
                    uid: user?.uid,
                  });
                  setShowWelcomeBackModal(shouldShowWelcomeBackModal);
                }
                if (!isImpersonated) {
                  setOnlineStatus(uid);
                  updateUserPlatforms(uid);
                  const analytics = await analyticsPromise;
                  if (analytics) {
                    setUserId(analytics, uid);
                  }
                }
              }
            }
          };
          onSuccess(user?.uid);
        })
        .catch((e) => {
          logError(e, 'fetch ip');
        });
      return true;
    },
    enabled: !!user?.uid,
  });

  const sendPhoneVerification = async (phoneNumber: string) => {
    const t = startPerfTrace('auth_sendPhoneVerification');
    if (typeof window === 'undefined') return '';
    // set a global recaptchaVerifier if it doesn't exist so that we don't recreate it. User was getting this error if had a failed attempt and then tried to resubmit with a new phone number: https://stackoverflow.com/questions/54110489/recaptcha-has-already-been-rendered-in-this-element
    if (!window.recaptchaVerifier) {
      let recaptchaContainer = document.getElementById('recaptcha-container');
      if (!recaptchaContainer) {
        recaptchaContainer = document.createElement('div');
        recaptchaContainer.id = 'recaptcha-container';
        recaptchaContainer.className = 'sr-only';
        document.body.prepend(recaptchaContainer);
      }
      window.recaptchaVerifier = new RecaptchaVerifier(
        AUTH,
        recaptchaContainer,
        { size: 'invisible' }
      );
    }
    const provider = new PhoneAuthProvider(AUTH);
    const verificationId = await provider.verifyPhoneNumber(
      phoneNumber,
      window.recaptchaVerifier
    );
    t.stop();
    return verificationId;
  };

  const signInWithPhone = async (verificationId: string, code: string) => {
    const t = startPerfTrace('auth_signInWithPhone');
    const credential = PhoneAuthProvider.credential(verificationId, code);
    const userCredential = await signInWithCredential(AUTH, credential);
    setUser(userCredential.user);
    t.stop();
    return userCredential;
  };

  const linkPhone = async (
    verificationId: string,
    verificationCode: string
  ) => {
    const t = startPerfTrace('auth_linkPhone');
    if (!AUTH.currentUser)
      throw new Error('An error occurred. Please try again.');
    try {
      const authCredential = PhoneAuthProvider.credential(
        verificationId,
        verificationCode
      );
      const cred = await _linkWithCredential(AUTH.currentUser, authCredential);
      t.stop();
      return cred;
    } catch (e) {
      t.stop({ error: 'true' });
      const err = e as FirebaseError;

      logError(err);

      switch (err.code) {
        case 'auth/invalid-verification-code':
          throw new Error('Invalid verification code');
        case 'auth/missing-verification-code':
          throw new Error('Missing verification code');
        case 'auth/code-expired':
          throw new Error('Verification code expired');
        case 'auth/credential-already-in-use':
          throw new Error('Phone number already in use');
        case 'auth/account-exists-with-different-credential':
          throw new Error('Phone number already in use');
        default:
          throw new Error('An unknown error occurred. Please try again.');
      }
    }
  };

  const getUserPhoneNumber = async () => {
    if (!AUTH.currentUser) throw new Error('No AUTH.currentUser');
    // Check if phone number exists in Firebase Auth user
    if (AUTH.currentUser.phoneNumber) return AUTH.currentUser.phoneNumber;
    // If not in auth, check if it's in the user document

    const userDoc = getValidUserDocument(
      AUTH.currentUser.uid,
      AUTH.currentUser.email ?? '',
      {
        name: AUTH.currentUser.displayName ?? 'Anonymous User',
        opted_out: false,
      },
      attribution
    );

    return userDoc?.phone || null;
  };

  const sendEmailVerification = async (user?: User | null) => {
    if (!user) user = AUTH.currentUser;
    if (!user) throw new Error('No AUTH.currentUser');
    const actionCodeSettings: ActionCodeSettings = {
      url: `${getHostUrl()}/verify-email`,
    };
    await _sendEmailVerification(user, actionCodeSettings);
  };

  const linkWithCredential = async (email: string) => {
    if (!AUTH.currentUser) throw new Error('No AUTH.currentUser');
    const credential = EmailAuthProvider.credential(
      email,
      window.location.href
    );
    await _linkWithCredential(AUTH.currentUser, credential);
  };

  const linkWithCredentialSocial = async (providerName: AuthProvider) => {
    if (!AUTH.currentUser) throw new Error('No AUTH.currentUser');
    const provider = initAuthProvider(providerName);

    try {
      const result = await linkWithPopup(AUTH.currentUser, provider);

      if (result && AUTH.currentUser) {
        // update the user's email to the email they used to sign in with
        updateUserEmail(result.user.email ?? '');

        return result;
      }

      return result;
    } catch (error) {
      const err = error as FirebaseError;

      if (err.code === 'auth/provider-already-linked') {
        return {
          user: AUTH.currentUser,
        };
      }

      throw error;
    }
  };

  const verifyEmail = useCallback((actionCode: string) => {
    return applyActionCode(AUTH, actionCode);
  }, []);

  useEffect(() => {
    if (user === undefined || userDoc === undefined) return;
    if (isSignInWithEmailLink(AUTH, window.location.href)) {
      if (user === null) {
        // different device
        const params = new URLSearchParams(window.location.search);
        // const params = window.location.href.split("?")[1].split("&")
        const customToken = params?.get('mxt');
        if (customToken) {
          const decodedCustomToken = decodeURIComponent(customToken);
          signInWithCustomToken(AUTH, decodedCustomToken)
            .catch((e) => {
              logError(
                e,
                'signInWithCustomToken',
                undefined,
                'Failed to sign in with custom token: ' + customToken
              );
              window.location.href = `/authwall?mxt=${customToken}&continueUrl=${
                window.location.href.split('?')[0]
              }`;
            })
            .then(() => {
              // remove mxt from url
              const url = new URL(window.location.href);
              url.searchParams.delete('mxt');
              window.history.replaceState({}, '', url.toString());
            });
        }
      } else if (user && userDoc) {
        const credential = EmailAuthProvider.credential(
          userDoc.email!,
          window.location.href
        );
        _linkWithCredential(user, credential).then(() => {
          logEvent('guest_account_linked', {}, userDoc.uid);
          const windowLocation = window.location.href.split('?')[0];
          window.location.href = windowLocation;
        });
      }
    }
  }, [user, userDoc]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(AUTH, (user) => {
      // set user from email/password login
      setUser(user);
      if (user) {
        user.getIdToken().then((token) => {
          Cookie.set('auth', token, { expires: 1 });
        });
        Cookie.set('uid', user.uid);
        // remove klayvio script if user is logged in
        const klaviyoScript = document.getElementById('klaviyo-popup');
        if (klaviyoScript) {
          klaviyoScript.remove();
        }
      } else if (user === null) {
        queryClient.setQueryData(['authUser'], null);
        Cookie.remove('auth');
        Cookie.remove('uid');
        setUser(null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient]);

  const refreshUserDoc = async (uid?: string) => {
    await queryClient.invalidateQueries({
      queryKey: ['authUser', uid ?? user?.uid],
    });
  };

  const handleAuthError = (e: unknown) => {
    if (e instanceof FirebaseError) {
      return e;
    }
    logError(e);
    throw new Error('Encountered unknown error in auth flow');
  };

  const loginSocial: FirebaseAuthFn<AuthProvider> = async (args) => {
    const t = startPerfTrace('auth_loginSocial');
    try {
      const inApp = checkInAppBrowser();
      if (inApp)
        throw new FirebaseError(
          'auth/invalid-browser',
          'Invalid Browser. Please open in this page in your default browser.'
        );
      setIsLoggingIn(true);
      const provider = initAuthProvider(args);
      if (isMobile()) {
        await setPersistence(AUTH, browserLocalPersistence);
      }
      const result = await signInWithPopup(AUTH, provider);

      if (!result) {
        throw new FirebaseError('auth/unknown', 'Unknown error');
      }

      setUser(result?.user);
      setIsLoggingIn(false);
      t.stop({ provider: args });
      return result;
    } catch (e) {
      setIsLoggingIn(false);
      t.stop({ provider: args, error: 'true' });
      return handleAuthError(e);
    }
  };

  const loginSocialRedirect = async (args: AuthProvider) => {
    const t = startPerfTrace('auth_loginSocialRedirect');
    try {
      const inApp = checkInAppBrowser();
      const provider = initAuthProvider(args);

      setIsLoggingIn(true);

      if (isMobile()) {
        await setPersistence(AUTH, browserLocalPersistence);
      }

      if (inApp) {
        const url = window.location.href;
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

        if (!newWindow) window.location.href = url;
        setIsLoggingIn(false);
        t.stop({ provider: args, openedNewWindow: 'true' });
        return;
      }

      const result = await signInWithRedirect(AUTH, provider);
      setIsLoggingIn(false);
      t.stop({ provider: args });
      return result;
    } catch (e) {
      setIsLoggingIn(false);
      t.stop({ provider: args, error: 'true' });
      return handleAuthError(e);
    }
  };

  const loginEmailPassword: FirebaseAuthFn<Credentials> = async (args) => {
    const t = startPerfTrace('auth_loginEmailPassword');
    try {
      setIsLoggingIn(true);
      if (isMobile()) {
        await setPersistence(AUTH, browserLocalPersistence);
      }
      const userCredential = await signInWithEmailAndPassword(
        AUTH,
        args.email,
        args.password
      );
      setUser(userCredential.user);
      setIsLoggingIn(false);
      t.stop();
      return userCredential;
    } catch (e) {
      setIsLoggingIn(false);
      t.stop({ error: 'true' });
      return handleAuthError(e);
    }
  };

  // admin feature
  const signInWithToken: FirebaseAuthFn<Credentials> = async (args) => {
    const t = startPerfTrace('auth_signInWithToken');
    try {
      setIsLoggingIn(true);
      const userCredential = await signInWithCustomToken(AUTH, args.email);
      setUser(userCredential.user);
      setIsLoggingIn(false);
      t.stop();
      return userCredential;
    } catch (e) {
      setIsLoggingIn(false);
      t.stop({ error: 'true' });
      return handleAuthError(e);
    }
  };

  const createAccountEmailPassword: FirebaseAuthFn<Credentials> = async (
    args
  ) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        AUTH,
        args.email,
        args.password
      );
      return userCredential;
    } catch (e) {
      return handleAuthError(e);
    }
  };

  const sendPasswordResetEmail = async (email: string) => {
    const actionCodeSettings: ActionCodeSettings = {
      url: `${getHostUrl()}/reset-password`,
    };
    await _sendPasswordResetEmail(AUTH, email, actionCodeSettings);
  };

  const resetPassword = async (oobCode: string, password: string) => {
    await confirmPasswordReset(AUTH, oobCode, password);
  };

  const updateUserPassword = async (
    oldPassword: string,
    newPassword: string
  ) => {
    if (AUTH.currentUser?.email && isAccountEmailPassword()) {
      const credential = EmailAuthProvider.credential(
        AUTH.currentUser.email,
        oldPassword
      );
      try {
        await reauthenticateWithCredential(AUTH.currentUser, credential);
        await updatePassword(AUTH.currentUser, newPassword);
      } catch (err) {
        throw new Error('Incorrect password');
      }
    }
  };

  const isAccountEmailPassword = () => {
    if (AUTH.currentUser) {
      return AUTH.currentUser.providerData.some(
        (p) => p.providerId === 'password'
      );
    }
    return false;
  };

  const updateUserEmail = (email: string) => {
    if (AUTH.currentUser) {
      updateEmail(AUTH.currentUser, email);
    }
  };

  const signInAnon = async () => {
    const user = await signInAnonymously(AUTH);
    await createUserDocument(
      getValidUserDocument(
        user.user.uid,
        user.user.email ?? '',
        {
          // firstName: 'Anonymous',
          // lastName: 'User',
          name: 'Anonymous User',
          opted_out: false,
        },
        attribution
      ),
      'anonymous'
    );
    setUser(user.user);
  };

  const observedProducts = useRef<string[]>([]);
  const observerTimeout = useRef<any | null>(null);
  const observeProduct = (productId: string) => {
    if (!observedProducts.current.includes(productId)) {
      observedProducts.current.push(productId);
    }
    if (observerTimeout.current) {
      clearTimeout(observerTimeout.current);
    }
    observerTimeout.current = setTimeout(() => {
      if (userDoc?.uid) {
        addToInteractions(
          userDoc.uid,
          'my_feed_viewed_real',
          observedProducts.current
        );
        observedProducts.current = [];
      }
    }, 1000);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userDoc,
        isLoadingUserDoc,
        isLoggingIn,
        logout,
        loginEmailPassword,
        loginSocial,
        loginSocialRedirect,
        signInWithToken,
        createAccountEmailPassword,
        sendEmailVerification,
        linkWithCredential,
        linkWithCredentialSocial,
        sendPasswordResetEmail,
        resetPassword,
        updateUserPassword,
        isAccountEmailPassword,
        verifyEmail,
        updateUserEmail,
        refreshUserDoc,
        sendPhoneVerification,
        signInWithPhone,
        linkPhone,
        showWelcomeBackModal,
        setShowWelcomeBackModal,
        observeProduct,
        signInAnon,
        attribution,
        getUserPhoneNumber,
        startPerfTrace,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthProvider;
